import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InnerWallType } from 'src/app/enums/innerWallType.enum';
import { WallSide } from 'src/app/enums/wallSide.enum';
import { IWall } from 'src/app/interfaces/wall.interface';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { NotificationService } from 'src/app/services/notification.service';
import { StepService } from 'src/app/services/step.service';
import { ThreeDService } from 'src/app/services/three-d.service';
import { StepFiveSliderComponent } from '../step-five/step-five-slider/step-five-slider.component';

@Component({
  selector: 'app-step-inner-walls',
  templateUrl: './step-inner-walls.component.html',
  styleUrls: ['./step-inner-walls.component.scss'],
})
export class StepInnerWallsComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  selectedWall: IWall;
  innerWallTypes = InnerWallType;
  openingQuestion = true;
  collided$: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('slider') slider: StepFiveSliderComponent;

  constructor(
    private configurationService: ConfigurationService,
    private threeDService: ThreeDService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private stepService: StepService,
    private cd: ChangeDetectorRef
  ) {}

  get canUseInnerWalls() {
    return this.configurationService.canUseInnerWalls;
  }

  get selectedWallIsInner() {
    if (!this.selectedWall) {
      return false;
    }
    return this.selectedWall.side === WallSide.inner;
  }

  get mirrored() {
    if (!this.selectedWall || !this.selectedWallIsInner) {
      return false;
    }
    return this.selectedWall.innerWall.rotated;
  }

  get canUseThreeway() {
    return this.configurationService.wallThickness > 60;
  }

  ngOnInit(): void {
    if (this.configurationService.configuration.settings.innerWalls.length > 0) {
      this.openingQuestion = false;
    }
    this.configurationService.setHideFrontWalls(true);

    this.threeDService.wallSelectionEnabled = true;
    this.threeDService.innerWallSelectionEnabled = true;
    this.subscription = this.threeDService.wallSelectionChanged$.subscribe(() => {
      this.selectedWall = this.threeDService.wallSelection.getSelectedWall();
      this.cd.detectChanges();
      this.updateSlider();
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.threeDService.wallSelectionEnabled = false;
    this.threeDService.innerWallSelectionEnabled = false;
  }

  showInnerWallOption(type: InnerWallType) {
    if (this.configurationService.wallThickness === 80 && type === InnerWallType.lProfile) {
      return false;
    }

    if (
      !this.configurationService.configuration.settings.innerWalls ||
      this.configurationService.configuration.settings.innerWalls.length === 0
    ) {
      return true;
    }
    return this.configurationService.configuration.settings.innerWalls[0].type === type;
  }

  addInnerWall(type: InnerWallType) {
    if (
      !!this.configurationService.configuration.settings.innerWalls &&
      this.configurationService.configuration.settings.innerWalls.some(
        i => i.placedOnSide % 2 !== this.selectedWall.side % 2
      )
    ) {
      this.notificationService.showNotification(
        this.translateService.instant('STEPINNERWALL.ONLYONEDIRECTION'),
        NotificationService.DEFAULT_TIMEOUT
      );
      return;
    }

    if (!this.showInnerWallOption(type)) {
      return;
    }
    if (type === InnerWallType.threeway && !this.canUseThreeway) {
      return;
    }
    const left = 600;
    this.threeDService.addInnerWall(type, left);
    this.configurationService.updateInnerWalls();
    this.cd.detectChanges();
  }

  newLeftPosition(value: number) {
    if (value === undefined || value === null) {
      return;
    }
    if (this.selectedWallIsInner && this.selectedWall.innerWall.left != value) {
      this.threeDService.setInnerWallPosition(value);
    }
    this.configurationService.updateInnerWalls();
  }

  updateSlider() {
    if (this.slider) {
      this.slider.positionFromLeftLimit = this.calculateRemainingSpaceToLeft();
      this.slider.positionFromRightLimit = this.calculateRemainingSpaceToRight();
      this.slider.setViewPosition();
      this.slider.minimumValue = 600;
      this.slider.minimumStep = 300;
    }
  }

  calculateRemainingSpaceToLeft(): number {
    if (this.selectedWall && this.selectedWallIsInner) {
      return this.selectedWall.innerWall.left;
    } else {
      return null;
    }
  }

  calculateRemainingSpaceToRight(): number {
    if (this.selectedWall && this.selectedWallIsInner) {
      const wallPlacedOn = this.threeDService.getWallBySide(this.selectedWall.innerWall.placedOnSide);
      const innerWallCompensation =
        this.configurationService.wallThickness +
        (this.configurationService.wallThickness <= 80
          ? this.configurationService.roundedWallCorrectionThickness * 2
          : 0);
      const space = wallPlacedOn.wallLength * 1000 - innerWallCompensation - this.selectedWall.innerWall.left;
      return Math.round(space);
    } else {
      return null;
    }
  }

  getInnerWallImage(innerWallType: InnerWallType) {
    if (innerWallType == null) {
      return null;
    }
    switch (innerWallType) {
      case InnerWallType.lProfile:
        return 'assets/images/l-profiel.png';
      case InnerWallType.threeway:
        if (this.configurationService.wallThickness <= 80) {
          return 'assets/images/drieweg-rounded.png';
        }
        return 'assets/images/drieweg-profiel.png';
    }
  }

  removeWall() {
    this.threeDService.removeSelectedInnerWall();
    this.configurationService.updateInnerWalls();
    this.selectedWall = null;
  }

  hideOpeningQuestion() {
    this.openingQuestion = false;
  }

  nextStep() {
    this.stepService.nextStep();
  }
}
